import { Credentials, RedirectUrlResponse } from '@quadminds/client-apis/auth';
import { useNavigateWithCurrentQueryParams } from '@quadminds/hooks';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { validateTokenExp, jwtDecode } from '@quadminds/shared/utils';
import { Dispatch, MutableRefObject, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useRedirect } from './useRedirect';
import { CONNECTION_ERROR_DATA } from '../config/errors';
import { useAuthValue } from '../context/AuthContext';
import { authApi, storkAuthApi } from '../services/api';
import { isSaas, isStorkUser, isMindy, isOms } from '../utils';

type UseLoginReturned = {
    /**  Whether is authenticating, fetching user data or redirecting. */
    isLoading: boolean;
    /**  Whether the user should be redirected to the desired application */
    hasToRedirect: boolean;
    /**  Controls whether the user should be redirected to the desired application
     *   is used internally by the hook and exposed in case it is needed
     */
    setHasToRedirect: Dispatch<SetStateAction<boolean>>;
    /**  The JWT access token.
     *   is used internally by the hook and exposed in case it is needed
     */
    token: string | null;
    /**  The JWT refresh token.
     */
    refreshToken?: string | null;
    /**  Indicates if there was a previous valid token stored in local storage
     *   is used internally by the hook and exposed in case it is needed
     */
    hadPreviousValidToken: MutableRefObject<boolean>;
    /**  Handles login with email and password */
    handleLogin: (values: Credentials) => Promise<string | undefined>;
    /**  Handles login with Google */
    handleLoginWithGoogle: () => Promise<string | undefined>;
    /**  Handles login with Microsoft */
    handleLoginWithMicrosoft: () => Promise<string | undefined>;
    /**  Handles login with Apple */
    handleLoginWithApple: () => Promise<string | undefined>;
    /**  Redirects the user to the desired application
     *   is used internally by the hook and exposed in case it is needed
     */
    doRedirect: (response: RedirectUrlResponse) => void;
    /**
     * Determines if the landing it's being used to login to the saas application
     * */
    isSaasLegacyLogin: boolean;
};

export const useLogin = (): UseLoginReturned => {
    // State
    const context = useAuthValue();
    const [hasToSelectOrganization, setHasToSelectOrganization] = useState(false);
    const [shouldGetUserInfo, setShouldGetUserInfo] = useState(false);
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [hasToRedirect, setHasToRedirect] = useState(false);
    const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
    const [refreshToken, setRefreshToken] = useState<string | null>(localStorage.getItem('refreshToken'));
    // OMS: intento de SSO silencioso con la cookie compartida (.quadminds.com)
    const [triedSilentSSO, setTriedSilentSSO] = useState(false);

    const hadPreviousValidToken = useRef<boolean>(Boolean(validateTokenExp(jwtDecode(localStorage.getItem('token')))));

    // Domain user info — use Stork auth API when the user authenticated via Stork
    const { data: authenticatedUser, isLoading: isGettingUserInfo } = useQuery<IAuthMeV2OutputDto>(
        ['getUserinfo', isStorkUser()],
        () => (isStorkUser() ? storkAuthApi.getUserInfo() : authApi.getUserInfo()),
        { enabled: shouldGetUserInfo },
    );

    // Handle redirect
    const { isLoading: isFetchingRedirectURL } = useRedirect(hasToRedirect, (response) => {
        setHasToRedirect(false);
        doRedirect(response);
    });

    // Additional Hooks
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const navigateWithCurrentQueryParams = useNavigateWithCurrentQueryParams();

    // Param used to ignore existing access tokens if any available
    const isLoggedOut = searchParams.get('logout');
    const redirectUrlQueryParam = searchParams.get('redirect');
    const forceOrgSelection = searchParams.get('selectOrganization');

    // Computed values
    const isLoading = useMemo(
        () =>
            Boolean(
                isAuthenticating ||
                    isFetchingRedirectURL ||
                    isGettingUserInfo ||
                    // OMS: mientras corre el SSO silencioso mostramos spinner (evita flash del login)
                    (isOms && !triedSilentSSO && isLoggedOut !== 'true' && !hadPreviousValidToken.current),
            ),
        [isAuthenticating, isFetchingRedirectURL, isGettingUserInfo, triedSilentSSO, isLoggedOut],
    );

    const isSaasLegacyLogin = Boolean(
        isSaas &&
            (redirectUrlQueryParam?.includes('https://saas.quadminds.com') ||
                redirectUrlQueryParam?.includes('https://test.quadminds.com')),
    );

    // Callbacks
    const handleLogin = useCallback(
        async (values: Credentials) => {
            try {
                setIsAuthenticating(true);
                const response = await context?.loginInWithEmailAndPassword(values);
                setIsAuthenticating(false);
                setToken(response?.accessToken || null);
                setRefreshToken(response?.refreshToken || null);
                setShouldGetUserInfo(true);
                if ((isMindy || isOms) && 'PasswordCredential' in window) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const PasswordCredentialCtor = (window as any).PasswordCredential;
                    const credential = new PasswordCredentialCtor({ id: values.email, password: values.password });
                    await navigator.credentials.store(credential);
                }
                return response?.accessToken;
            } catch (error) {
                setIsAuthenticating(false);
                throw error;
            }
        },
        [context],
    );

    const handleLoginWithGoogle = useCallback(async () => {
        try {
            setIsAuthenticating(true);
            const response = await context?.loginThroughGoogle();
            setIsAuthenticating(false);
            setToken(response?.accessToken || null);
            setRefreshToken(response?.refreshToken || null);
            setShouldGetUserInfo(true);
            return response?.accessToken;
        } catch (error) {
            setIsAuthenticating(false);
            throw error;
        }
    }, [context]);

    const handleLoginWithMicrosoft = useCallback(async () => {
        try {
            setIsAuthenticating(true);
            const response = await context?.loginThroughMicrosoft();
            setIsAuthenticating(false);
            setToken(response?.accessToken || null);
            setRefreshToken(response?.refreshToken || null);
            setShouldGetUserInfo(true);
            return response?.accessToken;
        } catch (error) {
            setIsAuthenticating(false);
            throw error;
        }
    }, [context]);

    const handleLoginWithApple = useCallback(async () => {
        try {
            setIsAuthenticating(true);
            const response = await context?.loginThroughApple();
            setIsAuthenticating(false);
            setToken(response?.accessToken || null);
            setRefreshToken(response?.refreshToken || null);
            setShouldGetUserInfo(true);
            return response?.accessToken;
        } catch (error) {
            setIsAuthenticating(false);
            throw error;
        }
    }, [context]);

    const doRedirect = useCallback(
        async (redirectUrl: RedirectUrlResponse) => {
            try {
                setIsAuthenticating(true);

                await context?.handleRedirect({
                    redirectResponse: redirectUrl,
                    hadPreviousValidToken,
                    redirectUrlQueryParam,
                });
                setIsAuthenticating(false);
            } catch (error) {
                setIsAuthenticating(false);
                navigate('/error', CONNECTION_ERROR_DATA);
            }
        },
        [context, redirectUrlQueryParam, navigate],
    );

    // Effects

    // Check whenever user may proceed to be redirected
    useEffect(() => {
        if (!authenticatedUser) return;
        const isCurrentlySelecting = location.pathname.includes('/select-organization');
        if (isCurrentlySelecting) return;

        const hasMultipleOrgs = authenticatedUser?.organizations && authenticatedUser.organizations.length > 1;
        if (hasMultipleOrgs) {
            setHasToSelectOrganization(true);
        } else {
            setHasToRedirect(true);
        }
    }, [authenticatedUser, location.pathname, setHasToRedirect, setHasToSelectOrganization]);

    // Handles when user has to select an organization
    useEffect(() => {
        if (hasToSelectOrganization) {
            // Redirect to select organization
            navigateWithCurrentQueryParams({
                newPath: '/select-organization', // redirect to select organization page
                ignoredParams: ['logout'], // ignore logout param so it does not cleanup the local storage token needed to change current organization
                additionalParams: { selectOrganization: 'true' }, // add selectOrganization param to avoid "smart-redirect" as the user is already logged in
            });
        }
    }, [hasToSelectOrganization, navigateWithCurrentQueryParams]);

    // Redirect if already has a valid token
    useEffect(() => {
        if (hadPreviousValidToken.current && !isLoggedOut && !forceOrgSelection) {
            setIsAuthenticating(false);
            setHasToRedirect(true);
        }
    }, [forceOrgSelection, hadPreviousValidToken, isLoggedOut]);

    // OMS: SSO silencioso cross-subdominio. Si no hay token local pero existe una
    // sesión SaaS (cookie compartida .quadminds.com), refreshToken() la usa para
    // mintear un accessToken → poblamos localStorage y disparamos el redirect
    // existente, sin pedir login. Si no hay sesión → cae al login normal.
    useEffect(() => {
        if (!isOms || hadPreviousValidToken.current || isLoggedOut === 'true' || triedSilentSSO) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await authApi.refreshToken();
                if (!cancelled && res?.accessToken) {
                    localStorage.setItem('token', res.accessToken);
                    if (res.refreshToken) localStorage.setItem('refreshToken', res.refreshToken);
                    hadPreviousValidToken.current = true;
                    setToken(res.accessToken);
                    setRefreshToken(res.refreshToken ?? null);
                    setHasToRedirect(true);
                }
            } catch {
                // sin sesión compartida → mostrar login
            } finally {
                if (!cancelled) setTriedSilentSSO(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isLoggedOut, triedSilentSSO]);

    // Handle logout if logout parameter is present
    useEffect(() => {
        if (isLoggedOut === 'true' && hadPreviousValidToken.current) {
            context?.logout();
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('isStorkUser');
            // Clear the hadPreviousValidToken ref since we just logged out
            hadPreviousValidToken.current = false;
        }
    }, [isLoggedOut, context]);

    return {
        isLoading,
        hasToRedirect,
        setHasToRedirect,
        token,
        refreshToken,
        hadPreviousValidToken,
        handleLogin,
        handleLoginWithGoogle,
        handleLoginWithMicrosoft,
        handleLoginWithApple,
        doRedirect,
        isSaasLegacyLogin,
    };
};
